import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import type { Page, WorkItem, WorkItemType } from '../../lib/types';
import {
  PRIORITY_TONE,
  READINESS_LABEL,
  READINESS_TONE,
  WAITING_STATUS_LABEL,
  WAITING_STATUS_TONE,
  WORK_ITEM_STATUS_TONE,
  formatDate,
  relativeDays,
  titleCase,
} from '../../lib/format';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  ErrorNotice,
  Loading,
  Table,
  Td,
  Th,
  Tr,
} from '../../components/ui';
import { WorkItemDialog } from '../WorkItemDialog';
import { CreateWorkItemDialog } from '../CreateWorkItemDialog';

/**
 * The Commitments and Follow-ups tabs on an account.
 *
 * For commitments the three §9 dates are shown side by side, because the whole
 * point of keeping them separate is being able to see the runway at a glance.
 */
export function AccountWorkTab({
  accountId,
  accountName,
  type,
}: {
  accountId: string;
  accountName: string;
  type: Extract<WorkItemType, 'customer_commitment' | 'customer_followup'>;
}) {
  const { me } = useSession();
  const [openOnly, setOpenOnly] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const query = useQuery({
    queryKey: ['work-items', accountId, type, openOnly],
    queryFn: () =>
      api.get<Page<WorkItem>>('/work-items', {
        accountId,
        type,
        openOnly: openOnly || undefined,
        limit: 200,
        sort: 'due',
      }),
  });

  const items = query.data?.data ?? [];
  const isCommitment = type === 'customer_commitment';

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <Checkbox
          label="Open only"
          checked={openOnly}
          onChange={(event) => setOpenOnly(event.target.checked)}
        />
        {me.capabilities.canCreateWorkItems && (
          <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
            {isCommitment ? 'Record a commitment' : 'Record what we are waiting for'}
          </Button>
        )}
      </div>

      {query.isLoading && <Loading />}
      {query.error && <ErrorNotice error={query.error} onRetry={() => query.refetch()} />}

      {query.data && items.length === 0 && (
        <EmptyState
          title={isCommitment ? 'No commitments' : 'Nothing outstanding with the customer'}
          description={
            isCommitment
              ? 'Nothing has been promised to this customer with a date attached.'
              : 'Nothing is currently waiting on the customer.'
          }
        />
      )}

      {items.length > 0 && (
        <Card padded={false}>
          <Table>
            <thead>
              <tr>
                <Th>Title</Th>
                <Th>Status</Th>
                {isCommitment ? (
                  <>
                    <Th>Customer date</Th>
                    <Th>Internal target</Th>
                    <Th>Readiness check</Th>
                    <Th>Readiness</Th>
                    <Th>Told customer</Th>
                  </>
                ) : (
                  <>
                    <Th>Waiting for</Th>
                    <Th>Follow up on</Th>
                    <Th>State</Th>
                  </>
                )}
                <Th>Owner</Th>
                <Th align="right">Priority</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const overdue =
                  item.status !== 'completed' &&
                  item.dueAt !== null &&
                  new Date(item.dueAt) < new Date();
                return (
                  <Tr key={item.id} onClick={() => setSelected(item.id)}>
                    <Td>
                      <span className="flex items-center gap-2">
                        <span className="font-medium text-ink">{item.title}</span>
                        {item.commitment?.leadershipVisibleAt && (
                          <Badge tone="critical" title={item.commitment.escalationReason ?? ''}>
                            Escalated
                          </Badge>
                        )}
                      </span>
                      {item.projectName && (
                        <span className="mt-0.5 block text-xs text-muted">{item.projectName}</span>
                      )}
                    </Td>
                    <Td>
                      <Badge tone={WORK_ITEM_STATUS_TONE[item.status]}>
                        {titleCase(item.status)}
                      </Badge>
                    </Td>
                    {isCommitment && item.commitment ? (
                      <>
                        <Td className={overdue ? 'font-medium text-critical' : undefined}>
                          {formatDate(item.commitment.customerDueAt)}
                          <span className="mt-0.5 block text-xs text-muted">
                            {relativeDays(item.commitment.customerDueAt)}
                          </span>
                        </Td>
                        <Td className="text-ink-2">
                          {formatDate(item.commitment.internalTargetAt)}
                        </Td>
                        <Td className="text-ink-2">{formatDate(item.commitment.internalCheckAt)}</Td>
                        <Td>
                          <Badge tone={READINESS_TONE[item.commitment.readiness]}>
                            {READINESS_LABEL[item.commitment.readiness]}
                          </Badge>
                        </Td>
                        <Td>
                          {item.commitment.externallyCommitted ? (
                            <Badge tone="serious" dot={false}>
                              Yes
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted">Internal</span>
                          )}
                        </Td>
                      </>
                    ) : (
                      <>
                        <Td className="max-w-64 text-ink-2">
                          <span className="line-clamp-2">{item.followup?.waitingFor}</span>
                        </Td>
                        <Td className={overdue ? 'font-medium text-critical' : undefined}>
                          {formatDate(item.followup?.followupAt)}
                          <span className="mt-0.5 block text-xs text-muted">
                            {relativeDays(item.followup?.followupAt)}
                          </span>
                        </Td>
                        <Td>
                          {item.followup && (
                            <Badge tone={WAITING_STATUS_TONE[item.followup.waitingStatus]}>
                              {WAITING_STATUS_LABEL[item.followup.waitingStatus]}
                            </Badge>
                          )}
                        </Td>
                      </>
                    )}
                    <Td className="text-xs text-ink-2">{item.ownerName}</Td>
                    <Td align="right">
                      <Badge tone={PRIORITY_TONE[item.priority]} dot={item.priority === 'critical'}>
                        {titleCase(item.priority)}
                      </Badge>
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        </Card>
      )}

      {selected && (
        <WorkItemDialog
          workItemId={selected}
          onClose={() => setSelected(null)}
          onChanged={() => query.refetch()}
        />
      )}
      {creating && (
        <CreateWorkItemDialog
          accountId={accountId}
          accountName={accountName}
          defaultType={type}
          onClose={() => setCreating(false)}
          onCreated={() => query.refetch()}
        />
      )}
    </>
  );
}
